/**
 * Pure helpers for TravelPayouts `sub_id` attribution — split out of
 * the hook file so Vitest can exercise them in a node-only environment
 * without pulling in the React Native module graph via
 * `usePreferences`.
 */

/**
 * Compose a TravelPayouts `sub_id` value for granular dashboard
 * attribution. Format `<surface>__<product>` lets us decompose the
 * funnel by both surface and product in the partner dashboard, in
 * addition to the PostHog-side `surface` / `product` event properties.
 */
export function buildSubId(surface: string, product: string): string {
  return `${surface}__${product}`;
}

/**
 * Append a `sub_id` query parameter to a TravelPayouts deep-link.
 *
 * The builders in `libs/affiliates` already embed `marker` and
 * (optionally) `trs`. Sub-IDs vary per call site, so threading them
 * through every input interface would inflate the surface area for one
 * value the builder never needs to validate. Appending post-hoc keeps
 * the lib pure and call sites trivial: `appendSubId(builder(...), id)`.
 *
 * Returns the original URL unchanged when:
 *   - the URL is empty / null (builder rejected the input upstream),
 *   - the sub_id value is empty (caller didn't compose one),
 *   - the URL already carries a `sub_id` query param (defensive — the
 *     builder shouldn't, but if a future provider adds one we don't
 *     want to silently overwrite it).
 *
 * Uses string concatenation rather than URL parsing so the existing
 * percent-encoding in the builder output is preserved exactly.
 */
export function appendSubId(url: string, subId: string): string {
  if (!url || !subId) return url;
  if (/[?&]sub_id=/.test(url)) return url;
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}sub_id=${encodeURIComponent(subId)}`;
}
